import {
  ButtonDS,
  DownloadOutlined,
  EmailOutlined,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  MessageTooltip,
  Spacer,
  usePersistMuiTableState,
  dateFormatterDayMonthLongYearAndHourMinute2Digits,
  dateFormatterDayMonthYearLong2Digits,
} from "@qivia/ui";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import "../../../../i18n/i18n";
import { useAppSelector } from "../../../../redux/hooks";
import {
  selectMonthlyReportsList,
  selectMonthlyReportsListStatus,
} from "../fleetSlice";
import {
  formatDate,
  formatDateTime,
  formatDateWithoutDay,
  getDate,
  getDateTime,
} from "../../../../libSlice/materialUiFormats";
import type { MonthlyReportDisplayed } from "../fleetApi";

export interface SharedInterfaceMonthlyReportsProps {
  canGenerateReports: boolean;
}

const pageName = "fleet.monthlyReports";
const gridStorageKey = "monthly-reports-grid";

export const SharedInterfaceMonthlyReports = (
  props: SharedInterfaceMonthlyReportsProps,
) => {
  const { canGenerateReports } = props;
  const { t } = useTranslation();

  const monthlyReportsListInit = useAppSelector(selectMonthlyReportsList);
  const monthlyReportsListStatus = useAppSelector(
    selectMonthlyReportsListStatus,
  );
  const apiRef = useGridApiRef();
  const dataGridState = usePersistMuiTableState(
    apiRef,
    gridStorageKey,
    pageName,
  );

  const [listDisplayed, setListDisplayed] = useState<
    MonthlyReportDisplayed[] | null
  >(null);

  const findKeysToTranslate = useCallback(
    (key: keyof Partial<MonthlyReportDisplayed>, value: string) => {
      switch (key) {
        case "month":
        case "createdAt":
          return dateFormatterDayMonthYearLong2Digits(new Date(value));
        case "sentAt":
          return dateFormatterDayMonthLongYearAndHourMinute2Digits(
            new Date(value),
          );
        default:
          return value;
      }
    },
    [],
  );

  const keysToTranslate = useMemo(
    () => ["month", "createdAt", "sentAt"] as (keyof MonthlyReportDisplayed)[],
    [],
  );

  const monthlyReportsList: MonthlyReportDisplayed[] = useMemo(() => {
    return monthlyReportsListInit.map((report) => {
      let newDict: MonthlyReportDisplayed & { id: string } = {
        ...report,
        id: report.uuid,
      };
      Object.entries(report).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof MonthlyReportDisplayed)) {
          newDict = {
            ...newDict,
            [key]: value
              ? findKeysToTranslate(
                  key as keyof MonthlyReportDisplayed,
                  value as string,
                )
              : "",
          };
        }
      });
      return newDict;
    });
  }, [monthlyReportsListInit, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (monthlyReportsListInit && monthlyReportsListStatus === "success") {
      setListDisplayed(monthlyReportsList);
    }
  }, [monthlyReportsList, monthlyReportsListInit, monthlyReportsListStatus]);

  const searchBarProps = {
    values: monthlyReportsList,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "uuid" as const,
      "fileName" as const,
      "bucketFileName" as const,
      "user" as const,
      "gapPercentage" as const,
    ],
  };

  const columnsDisplayed: {
    [index in keyof MonthlyReportDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      month: {
        type: "date",
        valueGetter: getDate,
        valueFormatter: formatDateWithoutDay,
      },
      createdAt: {
        type: "date",
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      sentAt: {
        type: "date",
        width: 210,
        valueGetter: getDateTime,
        valueFormatter: formatDateTime,
      },
      totalMileageGap: {
        type: "number",
        valueFormatter: (value: number) => {
          if (!value) return "";
          return `${value > 0 ? "+" : "-"} ${Math.abs(value)} km`;
        },
      },
      gapPercentage: {
        type: "number",
        width: 110,
        valueFormatter: (value: number) => {
          if (!value) return "";
          return `> ${value} %`;
        },
      },
      totalMileage: {
        type: "number",
      },
      driversConcerned: {
        width: 210,
        type: "number",
      },
      bucketFileName: {
        type: "string",
        valueFormatter: (value: string | null, row) => {
          if (!value) return "";
          return (
            <StyledColumnButton>
              <ButtonDS
                sizeButton={"S"}
                format={"hug"}
                buttonType={"secondary"}
                singleIcon={{ icon: <DownloadOutlined />, size: "S" }}
                onClick={() => {
                  console.log({
                    bucketFileName: value ?? "",
                    fileName:
                      "fileName" in row ? (row["fileName"] as string) : "",
                  });
                }}
              />
            </StyledColumnButton>
          );
        },
      },
    };
  }, []);

  if (!listDisplayed) {
    return <LaunchPage hasBorderRadius={true} />;
  }

  return (
    <StyledContainer>
      {canGenerateReports && (
        <MessageTooltip
          type="NEUTRAL"
          iconLeft={<EmailOutlined />}
          title={t("fleet.monthlyReports.recipientsBanner.title")}
          text={
            <>
              {t("fleet.monthlyReports.recipientsBanner.bodyBeforeLink")}
              <StyledLink href="/home/users/management">
                {t("fleet.monthlyReports.recipientsBanner.link")}
              </StyledLink>
              {t("fleet.monthlyReports.recipientsBanner.bodyAfterLink")}
            </>
          }
        />
      )}
      <Spacer y={1.5} />
      <MaterialUiTable<keyof MonthlyReportDisplayed, MonthlyReportDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  width: 100%;
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: inherit;
`;

const StyledColumnButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;
